import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import QuantityControl from '../helper/QuantityControl'
import { useDispatch, useSelector } from "react-redux";
import { IMAGE_URL } from "../../constants";
import { removeFromCart, updateQuantity, applyCoupon, removeCoupon } from "../../app/config/redux/slices/cartSlice";
import { useApplyCouponMutation } from '../../app/config/redux/slices/api/couponApiSlice';
import AlertMessage from './AlertMessage';
import CheckoutAuthModal from './CheckoutAuthModal';
import { useCancelOrderMutation } from '../../app/config/redux/slices/api/orderApiSlice';
import { clearOrder } from '../../app/config/redux/slices/orderSlice';

const CartSection = () => {
    const dispatch = useDispatch();

    const [discountAmount, setDiscountAmount] = useState(0);
    const [alert, setAlert] = useState(null);
    const [showCheckoutAuth, setShowCheckoutAuth] = useState(false);

    const {order} = useSelector((state) => state.order);
    const { cartItems, coupon } = useSelector(state => state.cart);
    const { userInfo } = useSelector((state) => state.auth);

    const isAuthenticated = !!userInfo?.access_token;
    const [couponCode, setCouponCode] = useState(coupon || "");
    const [cancelOrder, { cancelOrderIsLoading }] = useCancelOrderMutation();

    const handleCancelOrder = async () => {
        try{
            const res = await cancelOrder({
                data: {
                    id: order.payment_id,
                },
                token: userInfo.access_token
            }).unwrap();
            dispatch(clearOrder());

            setAlert({ type: 'success', message: "Successfully cancelled The Order" });

            setTimeout(() => {
                setAlert(null); // Auto close after 3s
            }, 3000);

        }catch(error){

            setAlert({ type: 'danger', message: "Failed to cancel The Order" });

            setTimeout(() => {
                setAlert(null); // Auto close after 3s
            }, 3000);

        }

    }
        
    useEffect(() => {
        if (isAuthenticated) {
             if (order) {
                handleCancelOrder();
            }
        }
    }, [order, isAuthenticated]);

    // Increase Quantity
    const increaseQuantity = (item) => {
        dispatch(updateQuantity({
            product_id: item.product.id, quantity: item.quantity + 1
        }))
    };

    // Decrease Quantity
    const decreaseQuantity = (item) => {
        if (item.quantity > 1) {
            dispatch(updateQuantity({
                product_id: item.product.id, quantity: item.quantity - 1
            }))
        } else {
            removeItem(item);
        }
    };

    // Remove Item
    const removeItem = (item) => {
        dispatch(removeFromCart(item.product.id))
    };

    const [applyCouponApi, { isLoading }] = useApplyCouponMutation();

    // 1. Dynamically calculate Subtotal
    const subtotal = cartItems.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0
    );

    // 2. DERIVED STATE: Always calculate finalAmount on every render
    // If subtotal drops to 0, final amount is 0 regardless of discount
    const finalAmount = Math.max(0, subtotal - discountAmount);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            showAlert('danger', "Please enter a coupon code.");
            return;
        }
        try {
            const res = await applyCouponApi({ 
                data: { code: couponCode, amount: subtotal }, 
                token: userInfo?.access_token
            }).unwrap();
            
            dispatch(applyCoupon(res.coupon.code));
            showAlert('success', res.message || "Coupon applied successfully!");
            setDiscountAmount(parseFloat(res.discount_amount || 0));
        } catch (err) {
            setDiscountAmount(0);
            showAlert('danger', err?.data?.message || err.message || "Something went wrong.");
        }
    };

    const handleRemoveCoupon = () => {
        dispatch(removeCoupon());
        setCouponCode("");
        setDiscountAmount(0);
    };

    // Helper function to handle auto-closing alerts cleanly
    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => {
            setAlert(null);
        }, 3000);
    };

    // 3. Keep coupon and backend in sync when subtotal/quantity changes
    useEffect(() => {
        if (coupon && subtotal > 0) {
            // Re-validate coupon with backend to get updated discount amount for new subtotal
            const reapplyCoupon = async () => {
                try {
                    const res = await applyCouponApi({ 
                        data: { code: coupon, amount: subtotal }, 
                        token: userInfo?.access_token
                    }).unwrap();
                    setDiscountAmount(parseFloat(res.discount_amount || 0));
                } catch (err) {
                    // If coupon becomes invalid due to subtotal change, remove it
                    handleRemoveCoupon();
                }
            };
            reapplyCoupon();
        } else if (subtotal === 0) {
            setDiscountAmount(0);
        }
    }, [subtotal, coupon, userInfo?.access_token]);

    return (
        <section className="cart py-80">
            {alert && (
                <AlertMessage
                    message={alert.message}
                    type={alert.type}
                    onClose={() => setAlert(null)}
                />
            )}
            {showCheckoutAuth && <CheckoutAuthModal onClose={() => setShowCheckoutAuth(false)} />}
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9 col-lg-8">
                        <div className="cart-table border border-gray-100 rounded-8">
                            <div className="cart-table-wrap">
                                <table className="table style-three cart-items-table">
                                    <thead>
                                        <tr>
                                            <th className="h6 mb-0 text-lg fw-bold">Product Name</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Price</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Quantity</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Subtotal</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Remove</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartItems.length === 0 ? (
                                            <tr className="cart-empty-row">
                                                <td colSpan="5">
                                                    <p className="text-center py-4">Cart is empty</p>
                                                </td>
                                            </tr>
                                        ) : (
                                            cartItems.map(item => (
                                                <tr key={item.product.id}>
                                                    <td data-label="Product">
                                                        <div className="table-product d-flex align-items-center">
                                                            <Link
                                                                to={`/product-details/${item.product.id}`}
                                                                className="table-product__thumb border border-gray-100 rounded-8 flex-center "
                                                            >
                                                                <img
                                                                    src={
                                                                        item.product.image_url
                                                                            ? item.product.image_url
                                                                            : item.product.image
                                                                                ? `${IMAGE_URL}/${item.product.image}`
                                                                                : "/assets/images/thumbs/product-two-img1.png"
                                                                    }
                                                                    srcSet={item.product.image_srcset || undefined}
                                                                    sizes="(max-width: 576px) 400px,
                                                                        (max-width: 992px) 800px,
                                                                        1200px"
                                                                    alt={item.product.name}
                                                                    loading="lazy"
                                                                />
                                                            </Link>
                                                            <div className="table-product__content text-start">
                                                                <h6 className="title text-lg fw-semibold mb-8">
                                                                    <Link
                                                                        to={`/product-details/${item.product.id}`}
                                                                        className="link text-line-2"
                                                                    >
                                                                        {item.product.name}
                                                                    </Link>
                                                                </h6>
                                                                {/* <div className="flex-align gap-16 mb-16">
                                                                <div className="flex-align gap-6">
                                                                    <span className="text-md fw-medium text-warning-600 d-flex">
                                                                        <i className="ph-fill ph-star" />
                                                                    </span>
                                                                    <span className="text-md fw-semibold text-gray-900">
                                                                        4.8
                                                                    </span>
                                                                </div>
                                                                <span className="text-sm fw-medium text-gray-200">
                                                                    |
                                                                </span>
                                                                <span className="text-neutral-600 text-sm">
                                                                    128 Reviews
                                                                </span>
                                                            </div>
                                                            <div className="flex-align gap-16">
                                                                <Link
                                                                    to="/cart"
                                                                    className="product-card__cart btn bg-gray-50 text-heading text-sm hover-bg-main-600 hover-text-white py-7 px-8 rounded-8 flex-center gap-8 fw-medium"
                                                                >
                                                                    Camera
                                                                </Link>
                                                                <Link
                                                                    to="/cart"
                                                                    className="product-card__cart btn bg-gray-50 text-heading text-sm hover-bg-main-600 hover-text-white py-7 px-8 rounded-8 flex-center gap-8 fw-medium"
                                                                >
                                                                    Videos
                                                                </Link>
                                                            </div> */}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Price">
                                                        <span className="text-lg h6 mb-0 fw-semibold">₹ {item.product.price}</span>
                                                    </td>
                                                    <td data-label="Quantity">
                                                        <QuantityControl
                                                            quantity={item.quantity}
                                                            onIncrement={() => increaseQuantity(item)}
                                                            onDecrement={() => decreaseQuantity(item)}
                                                        />
                                                    </td>
                                                    <td data-label="Subtotal">
                                                        <span className="text-lg h6 mb-0 fw-semibold">₹ {item.quantity * item.product.price}</span>
                                                    </td>
                                                    <td data-label="Remove">
                                                        <button
                                                            type="button"
                                                            onClick={() => removeItem(item)}
                                                            className="remove-tr-btn flex-align hover-text-danger-600"
                                                            aria-label={`Remove ${item.product.name} from cart`}
                                                        >
                                                            <i className="ph ph-x-circle text-2xl d-flex" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="cart-actions mt-16">
                                <div className="cart-coupon-form">
                                    <input
                                        type="text"
                                        className="common-input"
                                        placeholder="Coupon Code"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        disabled={!!coupon}
                                    />
                                    {!coupon ? (
                                        <button
                                            onClick={handleApplyCoupon}
                                            type="button"
                                            className="btn btn-main py-18 rounded-8"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? "Applying..." : "Apply Coupon"}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleRemoveCoupon}
                                            type="button"
                                            className="btn btn-danger py-18 rounded-8"
                                        >
                                            Remove Coupon
                                        </button>
                                    )}
                                </div>
                                {/* <button
                                    type="submit"
                                    className="text-lg text-gray-500 hover-text-main-600"
                                >
                                    Update Cart
                                </button> */}
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <div className="cart-sidebar border border-gray-100 rounded-8">
                            <h6 className="text-xl mb-32">Cart Totals</h6>
                            <div className="bg-color-three rounded-8 p-24">
                                <div className="mb-32 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">Subtotal</span>
                                    <span className="text-gray-900 fw-semibold">₹ {subtotal}</span>
                                </div>
                                <div className="mb-32 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">
                                        Estimate delivery charges on checkout
                                    </span>
                                </div>
                                {/* <div className="mb-0 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">
                                        Extimated Taxs
                                    </span>
                                    <span className="text-gray-900 fw-semibold">INR 0.00</span>
                                </div> */}
                                {discountAmount > 0 && (
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two">Discount</span>
                                        <span className="text-red-500 fw-semibold">- ₹ {discountAmount}</span>
                                    </div>
                                )}
                            </div>

                            <div className="bg-color-three rounded-8 p-24 mt-24">
                                <div className="flex-between gap-8">
                                    <span className="text-gray-900 text-xl fw-semibold">Total</span>
                                    <span className="text-gray-900 text-xl fw-semibold">₹ {finalAmount}</span>
                                </div>
                            </div>
                            {isAuthenticated ? (
                                <Link
                                    to="/checkout"
                                    className="btn btn-main mt-40 py-18 w-100 rounded-8"
                                >
                                    Proceed to checkout
                                </Link>
                            ) : (
                                <button
                                    className="btn btn-main mt-40 py-18 w-100 rounded-8"
                                    type="button"
                                    onClick={() => setShowCheckoutAuth(true)}
                                >
                                    Proceed to checkout
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CartSection
